import * as yup from 'yup'
import {phoneRegex, emailRegex} from '../../utils/helpers'

// The organization's type is required by the backend but not asked for here;
// nothing reads it beyond the list filter, so every new organization starts
// as home care and it can be changed from its General tab.
export const DEFAULT_ORGANIZATION_TYPE = 'home_care'

// Every dropdown here opens upwards, over what was already filled in, as the
// app's other forms do.
export const UP_MENU = {
    PaperProps: {style: {maxHeight: 320}},
    anchorOrigin: {vertical: 'top', horizontal: 'left'},
    transformOrigin: {vertical: 'bottom', horizontal: 'left'},
}

export const API_VERSIONS = [
    {value: 'v2', label: 'v2'},
    {value: 'v1', label: 'v1'},
]

// Lengths are the backend's: its column sizes, or its validation where it has
// any. Past them a save fails as a server error rather than a message here.
export const tooLong = max => `Must be at most ${max} characters`

export const optionalEmail = max =>
    yup
        .string()
        .max(max, tooLong(max))
        .test('email-optional', 'Invalid email address', value => !value || emailRegex.test(value))

export const optionalPhone = max =>
    yup
        .string()
        .max(max, tooLong(max))
        .test('phone-optional', 'Invalid phone number', value => !value || phoneRegex.test(value))

export const requiredEmail = max =>
    yup
        .string()
        .trim()
        .required('Email is required')
        .max(max, tooLong(max))
        .test('is-valid-email', 'Invalid email address', value => emailRegex.test(value))

export const digitsOf = value => (value || '').replace(/\D/g, '')

let nextKey = 1
const newKey = prefix => `${prefix}-${nextKey++}`

export const emptyPayer = () => ({payer_name: '', payer_type: '', service_category: '', provider_number: '', payer_code: ''})

export const emptyBranch = (main = false) => ({
    key: main ? 'main' : newKey('branch'),
    main,
    branch_name: '',
    email: '',
    phone_number: '',
    provider_number: '',
    umpi: '',
    address: {street1: '', street2: '', city: '', state: '', zip: ''},
    service_ids: [],
    payers: [],
    payerDraft: emptyPayer(),
    payerDraftOpen: true,
    evv: {office_qualifier: '', office_identifier: ''},
})

export const emptyUser = () => ({first_name: '', last_name: '', email: '', phone: '', role_id: '', branch_keys: ['main']})

export const hasContent = draft => Object.values(draft || {}).some(value => (Array.isArray(value) ? value.some(Boolean) : !!`${value ?? ''}`.trim()))

// The same payor on two branches is one payor of the organization: the
// backend keeps payors per organization, one per name and service category.
export const payerKey = payer => `${payer.payer_name.trim()}|${payer.service_category}`

export const branchNeedsEvv = (branch, evvServiceIds) => (branch?.service_ids || []).some(id => evvServiceIds.has(id))

// A branch code is required and unique within the organization but not asked
// for; one is made from the branch's name. The main branch keeps the default
// branch's code, which is how the rest of the app recognises it.
export const branchCodes = branches => {
    const taken = new Set()
    const codes = {}
    branches.forEach((branch, index) => {
        if (branch.main) return
        const base =
            branch.branch_name
                .trim()
                .toUpperCase()
                .replace(/[^A-Z0-9]+/g, '-')
                .replace(/^-+|-+$/g, '')
                .slice(0, 44) || `BRANCH-${index + 1}`
        let code = base
        for (let n = 2; taken.has(code); n++) code = `${base}-${n}`
        taken.add(code)
        codes[branch.key] = code
    })
    return codes
}

export const toBranchPayload = (branch, code) => ({
    branch_name: branch.branch_name.trim(),
    branch_code: code,
    provider_number: branch.provider_number.trim(),
    umpi: branch.umpi.trim(),
    email: branch.email.trim(),
    phone_number: branch.phone_number.trim(),
    status: 'active',
    addresses: [
        {
            address_type: 'office',
            street_line1: branch.address.street1,
            street_line2: branch.address.street2,
            city: branch.address.city,
            state: branch.address.state,
            zip_code: branch.address.zip,
        },
    ],
    services: branch.service_ids.map(id => ({service_id: id, is_active: true})),
})

// A draft (a payor or user being added) is checked on its own button, not by
// the step's form, so its problems are put on its fields by hand. Only the
// first per field is kept: an empty email is "required", not also "invalid".
export const showDraftErrors = (form, prefix, error) => {
    const seen = new Set()
    for (const problem of error.inner) {
        const path = `${prefix}.${problem.path.replace(/\[(\d+)\]/g, '.$1')}`
        if (seen.has(path)) continue
        seen.add(path)
        form.setError(path, {message: problem.message})
    }
}

export const labelOf = (options, value) => options.find(option => option.value === value)?.label || value || '—'
